import React, { useEffect, useMemo, useState } from "react";
import { useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import TransferOwnershipModal from "./components/organisation/TransferOwnershipModal";
import {
  ConfirmDialog,
  ErrorBox,
  Notice,
  PrimaryButton,
  RoleBadge,
  Section,
  Spinner,
} from "./components/organisation/OrgModal";
import {
  can,
  deleteOrganisation,
  fetchMe,
  fetchMembers,
  findNextOrganisation,
  formatOrgDate,
  NO_ORGANISATION_PATH,
  orgErrorMessage,
  readSession,
  startSession,
  switchOrganisation,
  updateOrganisation,
} from "./utils/organisationApi";
import { handleLogout } from "./components/auth/authHelpers";

const OrganisationSettingsPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const session = useMemo(() => readSession(), []);
  const isOwner = can(session, "transferOwnership");
  const canEditSettings = can(session, "manageSettings");

  const [organisation, setOrganisation] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [notice, setNotice] = useState("");
  // In a support session the row shows who owns the organisation, found in its member list
  const [owner, setOwner] = useState(null);

  const [name, setName] = useState("");
  const [nameError, setNameError] = useState("");
  const [savingName, setSavingName] = useState(false);


  const [isTransferOpen, setIsTransferOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteName, setDeleteName] = useState("");
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const canDelete = can(session, "deleteOrganisation");

  const load = async () => {
    setIsLoading(true);
    setLoadError("");
    try {
      const me = await fetchMe();
      const org = me?.organisation;
      if (!org) {
        setLoadError("Could not load organisation settings.");
        return;
      }
      setOrganisation({ ...org, role: me.role || session.role });
      setName(org.name);
    } catch (err) {
      setLoadError(orgErrorMessage(err, "Could not load organisation settings."));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (!session.isSupportSession) return undefined;
    let cancelled = false;
    fetchMembers()
      .then(({ members }) => {
        if (!cancelled) setOwner(members.find((member) => member.type === "member" && member.role === "owner") || null);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [session.isSupportSession]);

  const handleSaveName = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setNameError("Organisation name is required.");
      return;
    }
    if (trimmed.length > 120) {
      setNameError("Organisation name must be 120 characters or less.");
      return;
    }
    setSavingName(true);
    setNameError("");
    try {
      const saved = await updateOrganisation({ name: trimmed });
      setOrganisation((prev) => ({ ...prev, ...saved, role: prev?.role }));
      setName(saved.name || trimmed);
      setNotice("Organisation name saved.");
    } catch (err) {
      setNameError(orgErrorMessage(err, "Could not save the name."));
    } finally {
      setSavingName(false);
    }
  };

  const handleDelete = async () => {
    setDeleteBusy(true);
    setDeleteError("");
    // Found before deleting, while the token still works
    const next = await findNextOrganisation(organisation?.id);
    try {
      await deleteOrganisation();
    } catch (err) {
      setDeleteError(orgErrorMessage(err, "Could not delete the organisation."));
      setDeleteBusy(false);
      return;
    }
    if (!next) {
      window.location.assign(NO_ORGANISATION_PATH);
      return;
    }
    try {
      const token = await switchOrganisation(next.id);
      if (token) {
        startSession(token, "/");
        return;
      }
    } catch {
      // Sign out below if we cannot land in the other organisation
    }
    handleLogout();
  };

  const inputStyle = { borderColor: theme.palette.secondary.v4, color: theme.palette.text.main };

  return (
    <div
      className="flex flex-col w-full h-full overflow-auto"
      style={{ backgroundColor: theme.palette.primary.v2, color: theme.palette.text.main }}
    >
      <Notice onDismiss={() => setNotice("")}>{notice}</Notice>

      {isLoading ? (
        <Spinner label="Loading organisation..." />
      ) : loadError ? (
        <div className="m-6">
          <ErrorBox>{loadError}</ErrorBox>
        </div>
      ) : (
        <div className="w-full grid grid-cols-1 lg:grid-cols-2 gap-5 p-6 max-md:p-4">
          <Section title="Details">
            <dl className="grid grid-cols-1 lg:grid-cols-[10rem_1fr] gap-x-4 gap-y-3 text-sm mb-5">
              <dt className="text-accent-gray-light">Company domain</dt>
              <dd className="font-medium">{organisation.domain || "—"}</dd>
              {session.isSupportSession ? (
                <>
                  <dt className="text-accent-gray-light">Owner</dt>
                  <dd className="font-medium">
                    {owner ? (
                      <>
                        {owner.name || owner.email}
                        {owner.name && owner.email && <span className="block text-xs font-normal text-accent-gray-light">{owner.email}</span>}
                      </>
                    ) : (
                      "—"
                    )}
                  </dd>
                </>
              ) : (
                <>
                  <dt className="text-accent-gray-light">Your role</dt>
                  <dd>
                    <RoleBadge role={organisation.role || session.role} />
                  </dd>
                </>
              )}
              <dt className="text-accent-gray-light">Created</dt>
              <dd>{formatOrgDate(organisation.createdAt)}</dd>
            </dl>
            <label htmlFor="org-name" className="block text-sm font-medium mb-2">
              Organisation name
            </label>
            <div className="flex flex-wrap gap-2">
              <input
                id="org-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={120}
                disabled={!canEditSettings}
                className="flex-1 min-w-[12rem] px-3 py-1 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50"
                style={inputStyle}
              />
              {canEditSettings && (
                <PrimaryButton onClick={handleSaveName} disabled={savingName || name.trim() === organisation.name}>
                  {savingName ? "Saving..." : "Save"}
                </PrimaryButton>
              )}
            </div>
            {nameError && <p className="text-accent-red text-sm mt-1">{nameError}</p>}
          </Section>

          {isOwner && (
            <Section
              title="Ownership"
              description="There's exactly one Owner. You can hand ownership to an existing member, and you'll become an Admin."
            >
              <PrimaryButton onClick={() => setIsTransferOpen(true)}>Transfer Ownership</PrimaryButton>
            </Section>
          )}

          {/* {canDelete && (
            <Section
              title="Delete organisation"
              description="Everyone loses access. The company domain can sign up again."
            >
              <PrimaryButton
                danger
                onClick={() => {
                  setDeleteName("");
                  setDeleteError("");
                  setIsDeleteOpen(true);
                }}
              >
                Delete Organisation
              </PrimaryButton>
            </Section>
          )} */}

        </div>
      )}

      <ConfirmDialog
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        onConfirm={handleDelete}
        title="Delete Organisation"
        message={
          <p>
            Delete <span className="font-bold">{organisation?.name}</span>? Everyone loses access
            {organisation?.domain ? (
              <>
                , and <span className="font-bold">{organisation.domain}</span> can sign up again
              </>
            ) : (
              ", and the company domain can sign up again"
            )}
            .
          </p>
        }
        confirmLabel="Delete Organisation"
        busyLabel="Deleting..."
        busy={deleteBusy}
        error={deleteError}
        confirmText={organisation?.name}
        typedValue={deleteName}
        onTypedChange={setDeleteName}
      />

      <TransferOwnershipModal
        isOpen={isTransferOpen}
        onClose={() => setIsTransferOpen(false)}
        currentUserId={session.actorId}
        onTransferred={(member) => {
          setNotice(`${member.name || member.email} is now the Owner. Signing you back in as Admin...`);
          // The role in the token is now stale; get a fresh one for the same organisation
          switchOrganisation(organisation.id)
            .then((token) => (token ? startSession(token, "/settings?tab=organisation") : navigate(0)))
            .catch(() => handleLogout());
        }}
      />

    </div>
  );
};

export default OrganisationSettingsPage;
